import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ArrowRight, Calendar, Gift, Utensils, CreditCard, ShoppingBag, Store, Bike, Zap, Square, Circle, Sparkles, ChevronLeft, Moon, Users } from 'lucide-react';
import axios from 'axios';
import { getLocalizedPath } from '../utils/routes';
import { useTranslations } from '../context/TranslationsContext';
import EventsSection from '../components/EventsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default fallback hero slides - will use dynamic translations
const defaultHeroSlides = [
  {
    mediaUrl: '/images/hero.webp',
    mediaType: 'image',
    title: null, // Will use dynamic translation: hero.defaultTitle
    subtitle: null // Will use dynamic translation: hero.defaultSubtitle
  }
];

// Map backgroundColor to gradient class
const getGradientClass = (color) => {
  if (!color) return 'from-violet-500 to-purple-600';
  if (color.includes('10b981') || color.includes('emerald') || color.includes('teal')) return 'from-emerald-500 to-teal-600';
  if (color.includes('ec4899') || color.includes('pink') || color.includes('rose')) return 'from-pink-500 to-rose-600';
  if (color.includes('f59e0b') || color.includes('orange') || color.includes('amber')) return 'from-orange-500 to-amber-600';
  if (color.includes('3b82f6') || color.includes('blue')) return 'from-blue-500 to-indigo-600';
  return 'from-violet-500 to-purple-600';
};

// Announcement Slider Component - only shows when there are slides from Admin API
const AnnouncementSlider = ({ lang, sliderItems }) => {
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Only use items from database - no defaults
  const items = sliderItems || [];

  // Minimum swipe distance for detection
  const minSwipeDistance = 50;

  useEffect(() => {
    if (isPaused || items.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentAnnouncement((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length, isPaused]);

  const nextSlide = () => setCurrentAnnouncement((prev) => (prev + 1) % items.length);
  const prevSlide = () => setCurrentAnnouncement((prev) => (prev - 1 + items.length) % items.length);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Don't render anything if no slides from database
  if (items.length === 0) return null;

  const currentItem = items[currentAnnouncement];
  const current = {
    id: currentItem.slider_id,
    badge: lang === 'pl' ? 'Nowość' : 'New',
    title: currentItem.title?.[lang] || currentItem.title?.pl || '',
    desc: currentItem.subtitle?.[lang] || currentItem.subtitle?.pl || '',
    link: currentItem.buttonUrl || '#',
    btnText: currentItem.buttonText?.[lang] || currentItem.buttonText?.pl || (lang === 'pl' ? 'Sprawdź' : 'Check it out'),
    accentColor: getGradientClass(currentItem.backgroundColor),
    image: currentItem.imageUrl || currentItem.image || 'https://images.unsplash.com/photo-1621544402532-78c290378588?w=800&h=400&fit=crop',
    openInNewTab: currentItem.buttonOpenInNewTab || false
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-8 mb-20"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Fixed height slider with touch support */}
      <div
        className="glass-card overflow-hidden relative h-[220px]"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Background Image - positioned right with mask gradient fade */}
        <div className="absolute inset-0 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute right-0 top-0 bottom-0 w-3/5"
              style={{
                backgroundImage: `url(${current.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                maskImage: 'linear-gradient(to left, black 0%, black 20%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to left, black 0%, black 20%, transparent 100%)'
              }}
            />
          </AnimatePresence>
        </div>

        {/* Content */}
        <div className="relative z-10 p-8 h-full flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="max-w-md"
            >
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                {current.title}
              </h3>
              <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                {current.desc}
              </p>
              {current.openInNewTab || current.link.startsWith('http') ? (
                <a
                  href={current.link}
                  target={current.openInNewTab ? '_blank' : undefined}
                  rel={current.openInNewTab ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center gap-2 text-sm font-semibold py-2 rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors"
                >
                  {current.btnText}
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <Link
                  to={current.link}
                  className="inline-flex items-center gap-2 text-sm font-semibold py-2 rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors"
                >
                  {current.btnText}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation arrows - bottom right, visible on larger screens */}
        <div className="absolute bottom-4 right-4 hidden md:flex items-center gap-2 z-20">
          <button
            onClick={prevSlide}
            className="p-2 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="p-2 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Mobile swipe indicators (dots) */}
        {items.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex md:hidden items-center gap-2 z-20">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentAnnouncement(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentAnnouncement ? 'bg-primary w-4' : 'bg-white/40'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { td } = useTranslations(); // Database translations
  const [currentSlide, setCurrentSlide] = useState(0);
  const [news, setNews] = useState([]);
  const [heroSlides, setHeroSlides] = useState(defaultHeroSlides);
  const [sliderItems, setSliderItems] = useState([]);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const lang = i18n.language?.split('-')[0] || 'pl';

  // Helper for localized paths
  const lp = (key) => getLocalizedPath(key, lang);
  
  // Smart translate: try database first, fallback to i18next
  const tr = (key, fallback = null) => {
    const dbValue = td(key);
    if (dbValue === key) {
      return t(key, fallback || key);
    }
    return dbValue;
  };

  // Reset mediaLoaded when slide changes
  useEffect(() => {
    setMediaLoaded(false);
    // For images, preload them
    const currentMedia = heroSlides[currentSlide];
    if (currentMedia?.mediaType !== 'video') {
      const img = new Image();
      img.src = currentMedia?.mediaUrl || currentMedia?.imageUrl || currentMedia?.image;
      img.onload = () => setMediaLoaded(true);
      img.onerror = () => setMediaLoaded(true); // Show even if error
      // Fallback timeout - show after 2s regardless
      const timeout = setTimeout(() => setMediaLoaded(true), 2000);
      return () => clearTimeout(timeout);
    }
  }, [currentSlide, heroSlides]);

  // Fetch all content from Admin API
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [heroRes, sliderRes, blogRes] = await Promise.all([
        axios.get(`${API}/admin/hero`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/slider`).catch(() => ({ data: [] })),
        axios.get(`${API}/blog/posts?limit=3&sort=featured`).catch(() => ({ data: { posts: [] } }))
      ]);
      
      // Set hero slides - use API data or fallback to defaults
      if (heroRes.data && heroRes.data.length > 0) {
        setHeroSlides(heroRes.data);
      }
      
      // Set slider items
      if (sliderRes.data && sliderRes.data.length > 0) {
        setSliderItems(sliderRes.data);
      }
      
      // Set blog posts (from blog API)
      setNews(blogRes.data?.posts || []);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  // Hero slide timer
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const featuredCards = [
    { icon: Calendar, title: tr('discover.classes'), desc: tr('discover.classesDesc'), link: lp('classes'), color: 'from-violet-500 to-purple-600' },
    { icon: Gift, title: tr('discover.birthdays'), desc: tr('discover.birthdaysDesc'), link: lp('birthdays'), color: 'from-pink-500 to-rose-600' },
    { icon: CreditCard, title: tr('discover.passes'), desc: tr('discover.passesDesc'), link: lp('passes'), color: 'from-emerald-500 to-teal-600' },
    { icon: Utensils, title: tr('discover.restaurant'), desc: tr('discover.restaurantDesc'), link: 'https://aveburger.pl', color: 'from-orange-500 to-amber-600' }
  ];

  const moreCards = [
    { icon: Store, title: tr('moreCard.scootershop', lang === 'pl' ? 'Sklep hulajnogowy' : 'Scooter Shop'), link: 'https://scootive.pl', image: '/images/scootive.webp' },
    { icon: Store, title: tr('moreCard.bmxshop', lang === 'pl' ? 'Sklep BMX' : 'BMX Shop'), link: 'https://avebmx.pl', image: '/images/avebmx.webp' },
    { icon: Utensils, title: tr('moreCard.restaurant', lang === 'pl' ? 'Restauracja' : 'Restaurant'), link: 'https://aveburger.pl', image: '/images/aveburger.webp' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden" data-testid="hero-section">
        {/* Persistent gradient background - always visible */}
        <div className="absolute inset-0 z-0" style={{ background: 'var(--gradient-hero)' }} />
        
        {/* Background Slides */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: mediaLoaded ? 1 : 0, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: [0.33, 1, 0.68, 1] }}
            className="absolute inset-0 z-[1]"
          >
            {/* Support both video and image media types */}
            {heroSlides[currentSlide]?.mediaType === 'video' ? (
              <video
                className="absolute inset-0 w-full h-full object-cover"
                src={heroSlides[currentSlide].mediaUrl}
                autoPlay
                muted
                loop
                playsInline
                onLoadedData={() => setMediaLoaded(true)}
                onCanPlay={() => setMediaLoaded(true)}
              />
            ) : (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${heroSlides[currentSlide]?.mediaUrl || heroSlides[currentSlide]?.imageUrl || heroSlides[currentSlide]?.image})` }}
              />
            )}
            <div className="absolute inset-0" style={{ background: 'var(--gradient-overlay)' }} />
          </motion.div>
        </AnimatePresence>

        {/* Hero Gradient Overlay */}
        <div className="absolute inset-0 hero-gradient z-[2]" />

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-6 max-w-5xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
              >
                {/* Use custom title from Admin or fallback to dynamic translation */}
                <h1 className="fixed-title text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-['Syne'] font-extrabold mb-6 text-glow">
                  {heroSlides[currentSlide]?.title?.[lang] || heroSlides[currentSlide]?.title?.pl || tr('hero.defaultTitle', lang === 'pl' ? 'AvePark' : 'AvePark')}
                </h1>
                <p className="fixed-subtitle text-lg md:text-xl mb-10 font-medium tracking-wide">
                  {heroSlides[currentSlide]?.subtitle?.[lang] || heroSlides[currentSlide]?.subtitle?.pl || tr('hero.defaultSubtitle', lang === 'pl' ? 'Największy kryty skatepark w Warszawie' : 'Indoor Skatepark in Warsaw')}
                </p>
              </motion.div>
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              {/* Render custom buttons from Admin or default buttons */}
              {heroSlides[currentSlide]?.buttons && heroSlides[currentSlide].buttons.length > 0 ? (
                heroSlides[currentSlide].buttons.map((btn, idx) => (
                  <Link
                    key={idx}
                    to={btn.url || lp('book')}
                    target={btn.openInNewTab ? '_blank' : undefined}
                    rel={btn.openInNewTab ? 'noopener noreferrer' : undefined}
                    className={idx === 0 ? "btn-primary text-lg px-10 py-4 inline-flex items-center justify-center gap-2" : "btn-secondary text-lg px-10 py-4 inline-flex items-center justify-center gap-2"}
                    data-testid={`hero-btn-${idx}`}
                  >
                    {btn.text?.[lang] || btn.text?.pl || btn.text}
                    {idx === 0 && <ArrowRight className="w-5 h-5" />}
                  </Link>
                ))
              ) : (
                <>
                  <Link
                    to={lp('book')}
                    className="btn-primary text-lg px-10 py-4 inline-flex items-center justify-center gap-2"
                    data-testid="hero-cta-button"
                  >
                    {tr('hero.cta')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to={lp('park')}
                    className="btn-secondary text-lg px-10 py-4 inline-flex items-center justify-center gap-2"
                    data-testid="hero-explore-button"
                  >
                    {tr('hero.explore')}
                  </Link>
                </>
              )}
            </motion.div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-primary w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
              data-testid={`hero-slide-indicator-${index}`}
            />
          ))}
        </div>
      </section>

      {/* Featured Cards Section */}
      <section className="pt-20 px-5 md:px-12 max-w-7xl mx-auto" data-testid="featured-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-4">{tr('discover.featuredTitle')}</h2>
          <p className="text-muted-foreground text-lg">{tr('discover.featuredSubtitle')}</p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
  {featuredCards.map((card, index) => (
    <motion.div
      key={index}
      className="h-full"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
    >
      <Link
        to={card.link}
        className="glass-card p-8 h-full flex flex-col group card-hover"
        data-testid={`featured-card-${index}`}
      >
        <div
          className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}
        >
          <card.icon className="w-7 h-7 text-white" />
        </div>

        <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
          {card.title}
        </h3>

        <p className="text-gray-600 dark:text-gray-400 text-sm">
          {card.desc}
        </p>

        <div className="mt-auto pt-4 flex items-center gap-2 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          {tr('common.learnMore')} <ChevronRight className="w-4 h-4" />
        </div>
      </Link>
    </motion.div>
  ))}
</div>
      </section>

      <section className="px-5 md:px-12 max-w-7xl mx-auto" data-testid="annoucement-section">
        <div className="max-w-7xl mx-auto">
                    {/* Announcement Slider - now uses Admin API data */}
          <AnnouncementSlider lang={lang} sliderItems={sliderItems} />
          </div>
          </section>

      {/* Events Section */}
      <EventsSection />

      {/* Blog Section (previously News) */}
      <section className="py-20 px-5 md:px-12 max-w-7xl mx-auto" data-testid="blog-section">
        <div className="max-w-7xl mx-auto">

          {/* Blog Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-between items-center mb-12"
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-2">{tr('blog.title', 'Blog')}</h2>
              <p className="text-muted-foreground">{tr('blog.description', 'Aktualności i porady ze świata skateparku')}</p>
            </div>
            <Link
              to={`/${lang}/blog`}
              className="hidden md:flex items-center gap-2 text-primary hover:underline font-medium"
              data-testid="blog-view-all"
            >
              {tr('blog.goToBlog', 'Przejdź do bloga')} <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.length > 0 ? news.map((item, index) => (
              <motion.article
                key={item.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="glass-card overflow-hidden group card-hover"
                data-testid={`blog-card-${index}`}
              >
                <div className="aspect-video overflow-hidden bg-white/5">
                  {item.featuredImage ? (
                    <img
                      src={item.featuredImage}
                      alt={item.title?.[lang] || item.title?.pl}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-purple-600/20">
                      <span className="text-4xl font-bold text-white/20">
                        {(item.title?.[lang] || item.title?.pl || 'A')[0]}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold mb-2 line-clamp-2">
                    {item.title?.[lang] || item.title?.pl}
                  </h3>
                  <p className="text-muted-foreground text-sm line-clamp-2">
                    {item.excerpt?.[lang] || item.excerpt?.pl}
                  </p>
                  <Link
                    to={`/${lang}/blog/${item.slug}`}
                    className="mt-4 inline-flex items-center gap-2 text-primary text-sm font-medium"
                  >
                    {tr('common.readMore')} <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.article>
            )) : (
              /* Placeholder cards when no news */
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="glass-card p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">
                    {tr('discover.classes.title', lang === 'pl' ? 'Zajęcia' : 'Classes')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {tr('discover.classes.desc', lang === 'pl' 
                      ? 'Szkółki grupowe i indywidualne dla każdego poziomu' 
                      : 'Group and individual lessons for all levels')}
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="glass-card p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center mb-4">
                    <Gift className="w-6 h-6 text-pink-500" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">
                    {tr('discover.birthdays.title', lang === 'pl' ? 'Urodziny' : 'Birthdays')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {tr('discover.birthdays.desc', lang === 'pl'
                      ? 'Niezapomniane urodziny w skateparku'
                      : 'Unforgettable birthdays at the skatepark')}
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="glass-card p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                    <CreditCard className="w-6 h-6 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">
                    {tr('discover.passes.title', lang === 'pl' ? 'Karnety' : 'Passes')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {tr('discover.passes.desc', lang === 'pl'
                      ? 'Oszczędzaj z karnetami ilościowymi i okresowymi'
                      : 'Save with quantity and period passes')}
                  </p>
                </motion.div>
              </>
            )}
          </div>

          <Link
            to={`/${lang}/blog`}
            className="mt-8 md:hidden flex items-center justify-center gap-2 text-primary hover:underline font-medium"
          >
            {tr('home.goToBlog', 'Przejdź do bloga')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* More Than Skatepark Section */}
      <section className="py-20 px-5 md:px-12 max-w-7xl mx-auto" data-testid="more-section">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-4">{tr('notonly.title')}</h2>
            <p className="text-muted-foreground text-lg">{tr('notonly.subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {moreCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
              >
                <Link
                  to={card.link}
                  target="_blank"
                  className="relative aspect-[4/3] rounded-3xl overflow-hidden group block"
                  data-testid={`more-card-${index}`}
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center">
                        <card.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{card.title}</h3>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-5 md:px-12" data-testid="cta-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center glass-card p-12 md:p-16"
        >
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-6">
            {tr('common.readyToRide', lang === 'pl' ? 'Gotowy na jazdę?' : 'Ready to ride?')}
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            {tr('common.readyToRideSubtitle', lang === 'pl'
              ? 'Zarezerwuj swoją sesję online i oszczędź czas przy wejściu.'
              : 'Book your session online and save time at the entrance.')}
          </p>
          <Link
            to={lp('book')}
            className="btn-primary text-lg px-12 py-4 inline-flex items-center gap-2"
            data-testid="cta-book-button"
          >
            {tr('hero.cta')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;
